import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ConsultationForm from '../ConsultationForm';
import ProjectShareForm from '../ProjectShareForm';
import ReferencesSlider from '../ReferencesSlider';
import { API_URLS } from '../../config/api';
import { formatPrice, safeParseFloat } from '../../utils/format';
import * as Icons from 'lucide-react';
import { 
  ShoppingCart, 
  Check, 
  X, 
  Zap, 
  Crown, 
  Shield, 
  Globe, 
  Smartphone,
  CreditCard,
  BarChart3,
  Users,
  Headphones,
  Star,
  TrendingUp,
  Package,
  Settings,
  Rocket,
  ArrowRight,
  CheckCircle,
  Heart,
  Eye,
  Target,
  Lightbulb,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  MessageCircle
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import MediaDisplay from '../MediaDisplay';
const DEFAULT_CONTENT = {
  hero: {
    title: 'B2B E-Ticaret Çözümleri',
    description: 'İşletmeler arası ticaret için özel olarak tasarlanmış, güçlü ve ölçeklenebilir B2B e-ticaret platformları.',
    button1: { text: 'Ücretli Danışmanlık Al', action: 'open_consultation_modal', icon: 'Lightbulb' }
  },
  integration_sections: [
    {
      key: 'marketplace',
      title: 'B2B E-TİCARET ENTEGRASYONLARI',
      description:
        'B2B e-ticaret sitenizi, yurtiçi ile yurtdışı pazaryerlerindeki ve sosyal medya mağazalarınızla aynı panelden yönetin. Tek platformda satış, yönetim ve analitik süreçlerini optimize edin.',
      logos: [
        { name: 'Trendyol', image_url: 'https://prapazar.com/uploads/logoimage/images/trendyol-entegrasyonu.jpg' },
        { name: 'Hepsiburada', image_url: 'https://prapazar.com/uploads/logoimage/images/hepsiburada-entegrasyonu.jpg' },
        { name: 'Amazon', image_url: 'https://prapazar.com/uploads/logoimage/images/Prapazar - Amazon 1080x1080.png' },
        { name: 'N11', image_url: 'https://prapazar.com/uploads/logoimage/images/n11-entegrasyonu.jpg' },
        { name: 'Çiçeksepeti', image_url: 'https://prapazar.com/uploads/logoimage/images/ciceksepeti-entegrasyonu.jpg' },
        { name: 'Etsy', image_url: 'https://prapazar.com/uploads/logoimage/images/etsy-entegrasyonu.png' }
      ]
    },
    {
      key: 'erp',
      title: 'MUHASEBE, ERP VE E-FATURA ENTEGRASYONLARI',
      description:
        'Muhasebe, ERP ve e-fatura süreçlerinizi otomatikleştirerek zamandan tasarruf edin. Satıştan stok planlamasına kadar tüm verileri tek ekrandan yönetin.',
      logos: [
        { name: 'Logo', image_url: 'https://prapazar.com/uploads/logoimage/images/logo-entegrasyonu.jpg' },
        { name: 'Mikro', image_url: 'https://prapazar.com/uploads/logoimage/images/mikro-entegrasyonu.jpg' },
        { name: 'Paraşüt', image_url: 'https://prapazar.com/uploads/logoimage/images/paraşüt-entegrasyonu.jpg' },
        { name: 'QNB E-Fatura', image_url: 'https://prapazar.com/uploads/logoimage/images/qnb-e-fatura-entegrasyonu.jpg' },
        { name: 'N11 Faturam', image_url: 'https://prapazar.com/uploads/logoimage/images/n11-faturam-entegrasyonu.jpg' },
        { name: 'Foriba', image_url: 'https://prapazar.com/uploads/logoimage/images/foriba-e-fatura-entegrasyonu.jpg' }
      ]
    },
    {
      key: 'logistics',
      title: 'KARGO ve FULFILLMENT ENTEGRASYONLARI',
      description:
        'Kargo süreçlerinizi ve fulfillment operasyonlarınızı tek merkezden yönetin. Otomatik kargo barkodları, durum takipleri ve özel raporlamalarla süreçleri hızlandırın.',
      logos: [
        { name: 'Aras Kargo', image_url: 'https://prapazar.com/uploads/logoimage/images/aras-kargo-entegrasyonu.jpg' },
        { name: 'MNG Kargo', image_url: 'https://prapazar.com/uploads/logoimage/images/mng-kargo-entegrasyonu.jpg' },
        { name: 'Yurtiçi Kargo', image_url: 'https://prapazar.com/uploads/logoimage/images/yurtici-kargo-entegrasyonu.jpg' },
        { name: 'PTT Kargo', image_url: 'https://prapazar.com/uploads/logoimage/images/ptt_kargo_entegrasyonu.jpg' },
        { name: 'Oplog', image_url: 'https://prapazar.com/uploads/logoimage/images/oplog-kargo-entegrasyonu.jpg' },
        { name: 'Hepsilojistik', image_url: 'https://prapazar.com/uploads/logoimage/images/Hepsilojistik-Fulfillment-Entegrasyonu.png' }
      ]
    }
  ],
  faq: {
    title: 'Sıkça Sorulan Sorular',
    description: 'B2B e-ticaret hakkında merak ettiğiniz her şeyi burada bulabilirsiniz'
  },
  cta: {
    title: 'B2B Dijital Dönüşümünüzü Başlatın',
    description: 'Uzman ekibimiz ve güvenilir çözümlerimizle B2B dijital dünyada yerinizi alın.',
    primary_button: { text: 'Ücretli Danışmanlık Al', action: 'open_consultation_modal', icon: 'Lightbulb' },
    secondary_highlight: 'Uzman Danışmanlık Hizmeti'
  }
};

const resolveIcon = (name, fallback = 'Lightbulb') => {
  if (!name) return Lightbulb;
  if (typeof name !== 'string') return name;
  return (Icons[name] || Icons[fallback] || Lightbulb);
};

const B2BEcommercePage = () => {
  const navigate = useNavigate();
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showProjectShareForm, setShowProjectShareForm] = useState(false);
  const [ecommercePackages, setEcommercePackages] = useState([]);
  const [b2bPackages, setB2BPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [comparisonData, setComparisonData] = useState({ categories: [], packages: [], values: [] });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(API_URLS.PAGE_CONTENT('b2b-eticaret'));
        const data = await res.json();
        if (data.status === 'success') {
          setPageContent(data.data);
        } else {
          setPageContent(null);
        }
      } catch (error) {
        console.error('B2B e-ticaret içeriği yüklenemedi:', error);
        setPageContent(null);
      }
    };

    fetchContent();
  }, []);

  useEffect(() => {
    fetchPackages();
    fetchTimer();
    fetchComparisonData();
    fetchFaqs();
  }, []);

  const fetchPackages = async () => {
    try {
      const [ecomResponse, b2bResponse] = await Promise.all([
        fetch(API_URLS.ECOMMERCE_PACKAGES),
        fetch(API_URLS.B2B_PACKAGES)
      ]);
      
      const ecomData = await ecomResponse.json();
      const b2bData = await b2bResponse.json();
      
      setEcommercePackages(ecomData || []);
      setB2BPackages(b2bData || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimer = async () => {
    try {
      const response = await fetch(API_URLS.B2B_TIMER);
      const result = await response.json();
      if (result.status === 'success' && result.data && result.data.is_active == 1) {
        setTimer(result.data);
      }
    } catch (error) {
      console.error('Error fetching timer:', error);
    }
  };

  const fetchComparisonData = async () => {
    try {
      const response = await fetch(API_URLS.B2B_PACKAGE_COMPARISON);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        // Backend returns: { categories: [{category, features}], packages: [...], values: [...] }
        setComparisonData(result.data);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchFaqs = async () => {
    try {
      setFaqsLoading(true);
      const res = await fetch(API_URLS.FAQ);
      const json = await res.json();
      const categories = json.categories || [];
      const questions = json.questions || [];
      const b2bCat = categories.find(c => {
        const title = String(c.title || '').toLowerCase().trim();
        return title === 'b2b' || title === 'b2b e-ticaret' || title === 'işletmeler arası ticaret';
      });
      if (b2bCat) {
        const list = questions
          .filter(q => q.category_id === b2bCat.id)
          .sort((a,b) => (a.sort_order||0)-(b.sort_order||0))
          .map(q => ({ question: q.question, answer: q.answer }));
        setFaqs(list);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setFaqsLoading(false);
    }
  };

  // Helper function to get feature value for a package
  const getFeatureValue = (packageId, featureId) => {
    const value = comparisonData.values?.find(
      v => v.package_id == packageId && v.feature_id == featureId
    );
    return value || { value_type: 'cross', value_text: null };
  };

  // Render feature value icon/text
  const renderFeatureValue = (valueType, valueText) => {
    switch (valueType) {
      case 'check':
        return <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />;
      case 'cross':
        return <X className="w-5 h-5 text-gray-400 mx-auto" />;
      case 'optional':
        return <span className="text-orange-600 font-medium">İsteğe Bağlı</span>;
      case 'text':
        return <span className="font-medium text-gray-900">{valueText || '-'}</span>;
      default:
        return <X className="w-5 h-5 text-gray-400 mx-auto" />;
    }
  };

  // Timer countdown effect
  useEffect(() => {
    if (!timer || !timer.end_date) return;

    const calculateTimeLeft = () => {
      const difference = new Date(timer.end_date) - new Date();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  // B2B Packages as fallback
  const professionalPackages = [
    {
      id: 'pro-1',
      name: 'B2B Starter',
      description: 'B2B satışları için optimize edilmiş başlangıç paketi',
      price: { monthly: 1499, yearly: 14990 },
      originalPrice: { monthly: 1999, yearly: 19990 },
      discount: 25,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-50',
      icon: Users,
      popular: false,
      features: [
        { name: 'B2B Katalog', included: true },
        { name: 'Toptan Fiyatlandırma', included: true },
        { name: 'Bayi Paneli', included: true },
        { name: 'Kredi Limiti', included: true },
        { name: 'Özel Kampanyalar', included: true },
        { name: 'API Entegrasyonu', included: true },
        { name: 'Uzman Destek', included: true },
        { name: 'CRM Entegrasyonu', included: false }
      ],
      stats: { clients: '100+', volume: '₺5M+', rating: 4.6 }
    },
    {
      id: 'pro-2',
      name: 'B2B Growth',
      description: 'Büyük B2B operasyonlar için gelişmiş özellikler',
      price: { monthly: 2999, yearly: 29990 },
      originalPrice: { monthly: 3999, yearly: 39990 },
      discount: 25,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      icon: Rocket,
      popular: true,
      features: [
        { name: 'Gelişmiş B2B Katalog', included: true },
        { name: 'Dinamik Fiyatlandırma', included: true },
        { name: 'Multi-Bayi Sistemi', included: true },
        { name: 'Esnek Kredi Sistemi', included: true },
        { name: 'Otomatik Kampanyalar', included: true },
        { name: 'Full API Access', included: true },
        { name: 'Dedicaded Manager', included: true },
        { name: 'Advanced CRM', included: true }
      ],
      stats: { clients: '500+', volume: '₺25M+', rating: 4.8 }
    },
    {
      id: 'pro-3',
      name: 'B2B Enterprise',
      description: 'Enterprise seviye B2B e-ticaret platformu',
      price: { monthly: 4999, yearly: 49990 },
      originalPrice: { monthly: 6499, yearly: 64990 },
      discount: 23,
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-50',
      icon: Globe,
      popular: false,
      features: [
        { name: 'Enterprise B2B Suite', included: true },
        { name: 'AI Powered Pricing', included: true },
        { name: 'Global Multi-Region', included: true },
        { name: 'Advanced Credit Management', included: true },
        { name: 'ML Campaign Engine', included: true },
        { name: 'Custom Integration', included: true },
        { name: 'Enterprise Support', included: true },
        { name: 'White Label Solution', included: true }
      ],
      stats: { clients: '2K+', volume: '₺100M+', rating: 4.9 }
    },
    {
      id: 'pro-4',
      name: 'B2B Ultimate',
      description: 'Sınırsız B2B kapasitesi ve özel geliştirme',
      price: { monthly: 9999, yearly: 99990 },
      originalPrice: { monthly: 12999, yearly: 129990 },
      discount: 23,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      icon: Crown,
      popular: false,
      features: [
        { name: 'Unlimited Everything', included: true },
        { name: 'Custom AI Solutions', included: true },
        { name: 'Global Infrastructure', included: true },
        { name: 'Custom Banking Integration', included: true },
        { name: 'Personalized AI Engine', included: true },
        { name: 'Dedicated Development Team', included: true },
        { name: 'C-Level Support', included: true },
        { name: 'Custom Everything', included: true }
      ],
      stats: { clients: '10K+', volume: '₺500M+', rating: 5.0 }
    }
  ];

  // Use database B2B packages if available, otherwise fallback to hardcoded
  const displayB2BPackages = b2bPackages.length > 0 ? b2bPackages : professionalPackages;

  const mergedContent = useMemo(() => {
    const heroButton = {
      text: pageContent?.hero_button1_text || DEFAULT_CONTENT.hero.button1.text,
      link: pageContent?.hero_button1_link || DEFAULT_CONTENT.hero.button1.link,
      action: pageContent?.hero_button1_action || DEFAULT_CONTENT.hero.button1.action,
      icon: pageContent?.hero_button1_icon || DEFAULT_CONTENT.hero.button1.icon
    };

    const integrationSections =
      Array.isArray(pageContent?.additional_sections?.integration_sections) &&
      pageContent.additional_sections.integration_sections.length > 0
        ? pageContent.additional_sections.integration_sections
        : DEFAULT_CONTENT.integration_sections;

    const faqSection = {
      title: pageContent?.additional_sections?.faq?.title || DEFAULT_CONTENT.faq.title,
      description: pageContent?.additional_sections?.faq?.description || DEFAULT_CONTENT.faq.description
    };

    const cta = {
      ...DEFAULT_CONTENT.cta,
      ...(pageContent?.additional_sections?.cta || {}),
      primary_button: {
        ...DEFAULT_CONTENT.cta.primary_button,
        ...(pageContent?.additional_sections?.cta?.primary_button || {})
      }
    };

    return {
      hero: {
        title: pageContent?.hero_title || DEFAULT_CONTENT.hero.title,
        description: pageContent?.hero_description || DEFAULT_CONTENT.hero.description,
        button1: heroButton
      },
      integration_sections: integrationSections,
      faq: faqSection,
      cta
    };
  }, [pageContent]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-800 text-white py-24 relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              {mergedContent.hero.title}
            </h1>

            {mergedContent.hero.description && (
              <p className="text-xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
                {mergedContent.hero.description}
              </p>
            )}

            {mergedContent.hero.button1?.text && (
              <div className="flex justify-center">
                {/* Desktop: Ücretli Danışmanlık */}
                <Button
                  size="lg"
                  className="hidden md:flex mt-2 bg-white/15 hover:bg-white/20 text-white font-semibold px-8 py-3 border border-white/30"
                  onClick={() => {
                    if (mergedContent.hero.button1?.action === 'open_consultation_modal' || 
                        mergedContent.hero.button1?.text?.toLowerCase().includes('ücretli danışmanlık')) {
                      setShowConsultationForm(true);
                      return;
                    }
                    if (mergedContent.hero.button1?.link) {
                      window.location.href = mergedContent.hero.button1.link;
                    }
                  }}
                >
                  {(() => {
                    const HeroButtonIcon = resolveIcon(mergedContent.hero.button1.icon, 'Lightbulb');
                    return (
                      <>
                        <HeroButtonIcon className="w-5 h-5 mr-2" />
                        {mergedContent.hero.button1.text}
                      </>
                    );
                  })()}
                </Button>
                {/* Mobile: Tasarımları İncele */}
                <Button
                  size="lg"
                  className="md:hidden mt-2 bg-white/15 hover:bg-white/20 text-white font-semibold px-8 py-3 border border-white/30"
                  onClick={() => navigate('/web-sitesi-tasarimlari')}
                >
                  <Icons.Palette className="w-5 h-5 mr-2" />
                  Tasarımları İncele
                </Button>
              </div>
            )}

            {/* Timer */}
            {timer && (
              <div className="inline-block bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <h2 className="text-xl font-semibold mb-4 text-yellow-300">{timer.title}</h2>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-1">{String(timeLeft.days).padStart(2, '0')}</div>
                    <div className="text-sm text-purple-200">Gün</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-1">{String(timeLeft.hours).padStart(2, '0')}</div>
                    <div className="text-sm text-purple-200">Saat</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-1">{String(timeLeft.minutes).padStart(2, '0')}</div>
                    <div className="text-sm text-purple-200">Dakika</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-1 animate-pulse">{String(timeLeft.seconds).padStart(2, '0')}</div>
                    <div className="text-sm text-purple-200">Saniye</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* References Slider */}
      <div 
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <ReferencesSlider />
      </div>

      {/* Packages Section */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >

        {/* B2B Packages */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                B2B E-Ticaret Paketleri
              </span>
            </h2>
            <p className="text-xl text-gray-600 text-center">Büyük işletmeler ve B2B operasyonlar için özel çözümler</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600 mx-auto mb-3"></div>
                <p className="text-gray-600">Paketler yükleniyor...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {displayB2BPackages.slice(0, 4).map((pkg, index) => (
                <PackageCard 
                  key={pkg.id} 
                  pkg={pkg} 
                  index={index}
                />
              ))}
            </div>
          )}
        </div>

        {/* Package Details Button */}
        <div className="text-center mb-12">
          <Button
            onClick={() => setShowPackageDetails(!showPackageDetails)}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold"
          >
            {showPackageDetails ? 'Paket Detaylarını Gizle' : 'Tüm Paket Detaylarını Gör'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Package Comparison Table */}
        {showPackageDetails && (
          <div className="mb-20 bg-white rounded-2xl shadow-xl p-4 md:p-8">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6 md:mb-8">
              Paket Karşılaştırması
            </h3>
            
            {/* Mobile: Compact Accordion Layout */}
            <div className="md:hidden space-y-3">
              {displayB2BPackages.slice(0, 4).map((pkg) => (
                <Accordion key={pkg.id} type="single" collapsible className="border border-purple-200 rounded-lg">
                  <AccordionItem value={pkg.id} className="border-0">
                    <AccordionTrigger className="px-3 py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-3">
                        <div className="text-left flex-1">
                          <div className="font-bold text-gray-900 text-sm">{pkg.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{pkg.description || pkg.subtitle}</div>
                        </div>
                        <div className="text-right ml-3 flex-shrink-0">
                          <div className="font-bold text-purple-600 text-sm">{formatPrice(pkg.price || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                          <div className="text-xs text-gray-500">/yıllık</div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3">
                      {comparisonData.categories && comparisonData.categories.length > 0 ? (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {comparisonData.categories.map((categoryItem) => {
                            const categoryFeatures = categoryItem.features || [];
                            const category = categoryItem.category || categoryItem;
                            
                            if (categoryFeatures.length === 0) return null;
                            
                            const categoryKey = `${pkg.id}-${category.id || categoryItem.id}`;
                            const isExpanded = expandedCategories[categoryKey];
                            const visibleFeatures = isExpanded ? categoryFeatures : categoryFeatures.slice(0, 3);
                            
                            return (
                              <div key={category.id || categoryItem.id}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <h4 className="font-semibold text-gray-900 text-xs uppercase tracking-wide text-purple-600">
                                    {category.name || categoryItem.name}
                                  </h4>
                                  {categoryFeatures.length > 3 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedCategories(prev => ({
                                          ...prev,
                                          [categoryKey]: !prev[categoryKey]
                                        }));
                                      }}
                                      className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                                    >
                                      {isExpanded ? 'Daha az' : `+${categoryFeatures.length - 3} daha`}
                                    </button>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  {visibleFeatures.map((feature) => {
                                    const featureValue = getFeatureValue(pkg.id, feature.id);
                                    return (
                                      <div key={feature.id} className="flex items-center justify-between py-1 text-xs">
                                        <span className="text-gray-700 flex-1 truncate pr-2">{feature.name}</span>
                                        <div className="ml-2 flex-shrink-0">
                                          {renderFeatureValue(featureValue.value_type, featureValue.value_text)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <div className="font-bold text-blue-600 text-sm">{formatPrice(pkg.price || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} /yıllık</div>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
            
            {/* Desktop: Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 font-bold text-gray-900 min-w-[250px]">Özellikler</th>
                    {displayB2BPackages.slice(0, 4).map((pkg) => (
                      <th key={pkg.id} className="text-center p-3 font-bold text-gray-900 min-w-[120px]">
                        <div className="flex flex-col items-center space-y-1">
                          <span>{pkg.name}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Display dynamic features from database */}
                  {comparisonData.categories && comparisonData.categories.length > 0 ? (
                    comparisonData.categories.map((categoryItem) => {
                      // Get features for this category from the category object
                      const categoryFeatures = categoryItem.features || [];
                      
                      if (categoryFeatures.length === 0) return null;
                      
                      // Calculate colspan based on number of packages
                      const packageCount = displayB2BPackages.slice(0, 4).length;
                      const colspan = packageCount + 1; // +1 for the feature name column
                      
                      return (
                        <React.Fragment key={categoryItem.category?.id || categoryItem.id}>
                          {/* Category Header */}
                          <tr className="bg-gray-100">
                            <td colSpan={colspan} className="p-3 font-bold text-gray-900">
                              {categoryItem.category?.name || categoryItem.name}
                            </td>
                          </tr>
                          
                          {/* Features in this category */}
                          {categoryFeatures.map((feature) => (
                            <tr key={feature.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="p-3">
                                <div className="font-semibold">{feature.name}</div>
                                {feature.description && (
                                  <div className="text-xs text-gray-500 mt-1">{feature.description}</div>
                                )}
                              </td>
                              {displayB2BPackages.slice(0, 4).map((pkg) => {
                                const featureValue = getFeatureValue(pkg.id, feature.id);
                                return (
                                  <td key={`${pkg.id}-${feature.id}`} className="text-center p-3">
                                    {renderFeatureValue(featureValue.value_type, featureValue.value_text)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    // Fallback: Display price row if no comparison data
                    <tr className="border-b border-gray-200 hover:bg-gray-50 bg-blue-50">
                      <td className="p-3 font-semibold">Fiyat (Yıllık)</td>
                      {displayB2BPackages.slice(0, 4).map((pkg) => (
                        <td key={pkg.id} className="text-center p-3">
                          <div className="font-bold text-blue-600">{formatPrice(pkg.price || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} /yıllık</div>
                        </td>
                      ))}
                    </tr>
                  )}
                  
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {mergedContent.integration_sections.map((section, idx) => {
        const isAltLayout = idx % 2 === 1;
        const logos = Array.isArray(section.logos) ? section.logos : [];
        const sectionBg = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
        // E-TİCARET ENTEGRASYONLARI (marketplace) ve KARGO ENTEGRASYONLARI (logistics) için resmi yukarı al
        // E-TİCARET ENTEGRASYONLARI (marketplace), MUHASEBE/ERP (erp) ve KARGO ENTEGRASYONLARI (logistics) için resmi yukarı al
        const isMarketplaceOrLogistics = section.key === 'marketplace' || section.key === 'logistics' || section.key === 'erp';

        return (
          <section
            key={section.key || section.title || idx}
            className={`py-20 ${sectionBg}`}
            style={{
              opacity: contentReady ? 1 : 0,
              transition: 'opacity 0.6s ease-in'
            }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`flex flex-col ${isMarketplaceOrLogistics ? 'flex-col-reverse' : ''} lg:grid lg:grid-cols-2 gap-12 items-center`}>
                <div className={`${isAltLayout ? 'lg:order-2' : ''} text-center md:text-left`}>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    {section.title}
                  </h2>
                  {section.description && (
                    <p className="text-gray-700 text-center md:text-justify mb-4 leading-relaxed">
                      {section.description}
                    </p>
                  )}
                </div>
                <div className={`${isAltLayout ? 'lg:order-1' : ''}`}>
                  <div className="grid grid-cols-3 gap-6">
                    {logos.map((logo, logoIdx) => (
                      <div
                        key={logo.id || logo.key || `${logo.name || 'logo'}-${logoIdx}`}
                        className="group flex justify-center"
                      >
                        <MediaDisplay
                          src={logo.image_url || logo.img || logo.url}
                          alt={logo.name || 'Logo'}
                          className="w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-300"
                          videoProps={{ controls: true }}
                          imgProps={{
                            onError: (e) => {
                              e.currentTarget.style.opacity = '0.3';
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* FAQ Section */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {mergedContent.faq.title}
            </h2>
            {mergedContent.faq.description && (
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                {mergedContent.faq.description}
              </p>
            )}
          </div>

          <div className="max-w-4xl mx-auto">
            {faqsLoading ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Yükleniyor...</p>
              </div>
            ) : !faqs.length ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Bu kategori için soru bulunamadı.</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-purple-600" />
                        {f.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA - Digital Transformation */}
      <section 
        className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">
              {mergedContent.cta.title}
            </h2>
            {mergedContent.cta.description && (
              <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
                {mergedContent.cta.description}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {mergedContent.cta.primary_button?.text && (
                <Button
                  onClick={() => {
                    if (mergedContent.cta.primary_button?.action === 'open_consultation_modal') {
                      setShowConsultationForm(true);
                      return;
                    }
                    if (mergedContent.cta.primary_button?.link) {
                      window.location.href = mergedContent.cta.primary_button.link;
                    }
                  }}
                  className="inline-flex items-center px-8 py-4 h-[56px] bg-white text-purple-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  {(() => {
                    const CtaIcon = resolveIcon(mergedContent.cta.primary_button.icon, 'ShoppingCart');
                    return (
                      <>
                        <CtaIcon className="w-5 h-5 mr-2" />
                        {mergedContent.cta.primary_button.text}
                      </>
                    );
                  })()}
                </Button>
              )}

              <a
                href={`https://api.whatsapp.com/send/?phone=905324404011&text=${encodeURIComponent('Merhaba, B2B e-ticaret hizmetleriniz hakkında bilgi almak istiyorum.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 h-[56px] bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp İletişim
              </a>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Users className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">B2B E-Ticaret Çözümleri</h3>
                <p className="text-purple-100">İşletmeler arası profesyonel satış platformları</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <TrendingUp className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">B2B Satışlarınızı Artırın</h3>
                <p className="text-purple-100">Optimize edilmiş B2B kullanıcı deneyimi</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Headphones className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">7/24 Destek</h3>
                <p className="text-purple-100">Uzman B2B e-ticaret ekibimiz yanınızda</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
          <div className="relative z-50 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <ConsultationForm
              isModal={true}
              onClose={() => setShowConsultationForm(false)}
            />
          </div>
        </div>
      )}

      {/* Project Share Modal */}
      {showProjectShareForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
          <div className="relative z-50 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <Button
              onClick={() => setShowProjectShareForm(false)}
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white w-10 h-10 p-0 rounded-full"
            >
              ✕
            </Button>
            <ProjectShareForm onClose={() => setShowProjectShareForm(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

const PackageCard = ({ pkg, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Icon mapping for database packages
  const iconMap = {
    'Package': Package,
    'TrendingUp': TrendingUp,
    'BarChart3': BarChart3,
    'Crown': Crown,
    'Users': Users,
    'Rocket': Rocket,
    'Globe': Globe,
    'Star': Star
  };
  
  // Get icon component
  const IconComponent = typeof pkg.icon === 'string' ? (iconMap[pkg.icon] || Package) : pkg.icon;
  
  // Parse price if it's from database
  const packagePrice = safeParseFloat(pkg.price || 0);
  const packageOriginalPrice = safeParseFloat(pkg.original_price || 0);
  
  // Parse discount
  const packageDiscount = parseFloat(pkg.discount || 0);
  
  // Parse features if they're JSON strings
  let packageFeatures = [];
  try {
    if (typeof pkg.features === 'string') {
      packageFeatures = JSON.parse(pkg.features || '[]');
    } else if (Array.isArray(pkg.features)) {
      packageFeatures = pkg.features;
    }
    // Ensure included is boolean
    packageFeatures = packageFeatures.map(f => ({
      ...f,
      included: Boolean(f.included)
    }));
  } catch (e) {
    console.error('Error parsing features:', e);
    packageFeatures = [];
  }

  return (
    <Card
      className={`relative border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer flex flex-col h-full ${
        (pkg.popular === true || pkg.popular === 1 || pkg.popular === '1') ? 'ring-4 ring-purple-200 scale-105' : ''
      } ${pkg.bgColor}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        animationDelay: `${index * 0.1}s`,
        animation: 'fadeInUp 0.6s ease-out forwards'
      }}
    >
      {/* Popular Badge */}
      {(pkg.popular === true || pkg.popular === 1 || pkg.popular === '1') && (
        <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
          <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold animate-pulse" style={{ width: '150px' }}>
            <Crown className="w-4 h-4 mr-1" />
            EN POPÜLER
          </Badge>
        </div>
      )}

      {/* Background Gradient Animation */}
      <div className={`absolute inset-0 bg-gradient-to-br ${pkg.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

      <CardHeader className="space-y-4 pb-6 flex-shrink-0">
        <div className="space-y-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${pkg.color} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
            <IconComponent className="w-8 h-8 text-white animate-pulse" />
          </div>
          
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors">
              {pkg.name}
            </CardTitle>
            <p className="text-gray-600 leading-relaxed">{pkg.description || pkg.subtitle}</p>
          </div>
        </div>

        {/* Pricing */}
        <div className="space-y-2">
          <div className="flex items-baseline space-x-2">
            <span className="text-4xl font-bold text-gray-900">
              ₺{packagePrice.toLocaleString()}
            </span>
            <span className="text-gray-600">/yıllık</span>
          </div>
          
          {packageDiscount > 0 && packageOriginalPrice > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-lg text-gray-400 line-through">
                ₺{packageOriginalPrice.toLocaleString()}
              </span>
              <Badge variant="destructive" className="animate-bounce">
                %{packageDiscount} İndirim
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col flex-1">
        {/* Features - flex-grow ile alan doldurur */}
        <div className="space-y-3 flex-grow">
          {packageFeatures.map((feature, idx) => (
            <div 
              key={idx} 
              className="flex items-center space-x-3 transition-all duration-300"
              style={{
                animationDelay: `${(index * 0.1) + (idx * 0.05)}s`,
                animation: isHovered ? 'slideInRight 0.3s ease-out forwards' : 'none'
              }}
            >
              {feature.included ? (
                <CheckCircle className="w-5 h-5 text-green-500 animate-pulse flex-shrink-0" />
              ) : (
                <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
              )}
              <span className={`text-sm ${feature.included ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                {feature.name}
              </span>
            </div>
          ))}
        </div>

        {/* CTA Button - Her zaman en altta */}
        <div className="mt-6 flex-shrink-0">
          <Button 
            onClick={() => {
              if (pkg.url) {
                window.open(pkg.url, '_blank');
              } else {
                window.location.href = '/iletisim';
              }
            }}
            className="w-full h-12 font-semibold transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            <span>Satın Al</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </CardContent>

      {/* Hover Animation Particles */}
      {isHovered && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
          <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-blue-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
          <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
        </div>
      )}
    </Card>
  );
};

export default B2BEcommercePage;

